/**
 * Tenant-authored booking-policy config, stored as a single JSONB column
 * (`business_settings.booking_policy_config`). One object holds all three
 * related toggles so a tenant can set any of them independently without a
 * schema change per toggle.
 *
 * A NULL column (the default) means "everything off": no acceptance checkbox,
 * auto-generated cancellation bullets, and the static /terms-of-service link —
 * i.e. the historical behavior. Each field below is independently optional.
 *
 * This module is deliberately self-contained (no server/client imports) so it
 * can be used by both the server loader and the client booking form.
 */

/** Bilingual free-text block. */
export interface BilingualPolicyText {
  en?: string | null;
  ar?: string | null;
}

export interface BookingPolicyConfig {
  /** When true, the booking page gates every booking path behind an acceptance
   *  checkbox shown above the pay button. */
  requireAcceptance?: boolean;
  /** Overrides the auto-generated cancellation bullets, per language. */
  cancellationPolicy?: BilingualPolicyText;
  /** Renders terms in a drawer instead of the static link, per language. */
  termsConditions?: BilingualPolicyText;
}

/**
 * Normalized, client-ready view of the config. Booleans/strings only — never
 * the raw JSONB — so the loader can hand this straight to the form.
 */
export interface ResolvedBookingPolicy {
  requireAcceptance: boolean;
  /** Custom cancellation policy text keyed by language ('' when unset). */
  cancellationPolicy: { en: string; ar: string };
  /** Custom terms & conditions text keyed by language ('' when unset). */
  termsConditions: { en: string; ar: string };
}

const EMPTY_POLICY: ResolvedBookingPolicy = {
  requireAcceptance: false,
  cancellationPolicy: { en: '', ar: '' },
  termsConditions: { en: '', ar: '' },
};

function cleanText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Coerce a bilingual jsonb value to trimmed strings. Non-string values (jsonb
 *  carries no column-level type enforcement) collapse to ''. Exported for other
 *  readers of bilingual jsonb blocks, e.g. the settings page's class note.
 *
 *  A bare STRING is a pre-bilingual value (the membership approval/rejection
 *  copy was one string sent to every customer regardless of language) and is
 *  returned for BOTH languages — which is exactly what those customers receive
 *  today, and what the admin should therefore see in both boxes. Saving once
 *  converts the row to { en, ar }. The email side does the same via
 *  pickLegacyOrBilingual in @kit/shared/email. */
export function cleanBilingual(value: unknown): { en: string; ar: string } {
  // NOTE: the legacy single-string branch was added for the membership email
  // copy migration, but this helper is ALSO used by resolveBookingPolicyConfig
  // for customer-facing booking-page copy, which never had a single-string
  // shape. That is harmless (the branch simply never matches there) — but if a
  // future field must treat a bare string as invalid rather than as legacy,
  // give it its own reader instead of narrowing this one, which the membership
  // migration depends on.
  if (typeof value === 'string') {
    const text = value.trim();
    return { en: text, ar: text };
  }

  const obj = (value ?? {}) as Record<string, unknown>;
  return { en: cleanText(obj.en), ar: cleanText(obj.ar) };
}

/**
 * Parse the raw JSONB column into a normalized policy. Defensive against any
 * shape (NULL, wrong types, partial objects) — anything unrecognized degrades
 * to the "off" default rather than throwing.
 */
export function resolveBookingPolicyConfig(
  raw: unknown,
): ResolvedBookingPolicy {
  if (!raw || typeof raw !== 'object') {
    return EMPTY_POLICY;
  }

  const config = raw as BookingPolicyConfig;

  return {
    requireAcceptance: config.requireAcceptance === true,
    cancellationPolicy: cleanBilingual(config.cancellationPolicy),
    termsConditions: cleanBilingual(config.termsConditions),
  };
}

/**
 * Pick the custom text for the active language, falling back to the other
 * language when the active one is blank (so a tenant who only wrote English
 * still shows *something* to Arabic viewers rather than reverting to defaults).
 * Returns '' when neither language is set — the caller then uses its default.
 *
 * DELIBERATELY OPPOSITE to pickTenantNote in @kit/shared/email, even though the
 * same BilingualCopyBlock edits both:
 *
 *   - POLICY text (this function) — cancellation policy, terms. There is no
 *     built-in localized default worth showing, so a legal notice in the wrong
 *     language beats no legal notice at all. Cross-language fallback ON.
 *   - EMAIL notes (pickTenantNote) — class confirmation, booking cancellation.
 *     A properly localized default already exists, so showing an Arabic reader
 *     the tenant's English paragraph is strictly worse. Fallback OFF.
 *
 * The only user-visible signal is the helper prose ("Leave BOTH languages
 * empty..." vs "Leave A language empty..."), so keep those strings accurate if
 * either rule changes.
 */
export function pickPolicyText(
  text: { en: string; ar: string },
  language: string,
): string {
  const isArabic = language === 'ar';
  const primary = isArabic ? text.ar : text.en;
  const fallback = isArabic ? text.en : text.ar;
  return primary || fallback;
}

/** Split a free-text block into bullet lines (drops blank lines). */
export function policyTextToBullets(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
